/* eslint-disable react-hooks/exhaustive-deps */
import React, {FC, useEffect, useState, useCallback} from 'react'
import {Dropdown, FormControl, Spinner} from 'react-bootstrap'
import useAxiosPrivate from '../../../../libs/useAxiosPrivate'
import {getHSCodesFromHSDescriptions} from '../core/_requests'
import lodash from 'lodash'

type HSDropdownWithLoadMoreProps = {
  onSelect: (value: string) => void
  disabled?: boolean
  value?: string
  placeholder?: string
}

type HSCodeType = {
  data: string
}

const HSDropdownWithLoadMore: FC<HSDropdownWithLoadMoreProps> = ({
  onSelect,
  disabled = false,
  value,
  placeholder = 'Select...',
}) => {
  const axiosPrivate = useAxiosPrivate()
  const [items, setItems] = useState<Array<HSCodeType>>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedValue, setSelectedValue] = useState<string | undefined>(value)
  const [offset, setOffset] = useState<number>(0)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)

  const limit = 50

  const fetchData = async (search: string, currentOffset: number, isAppending: boolean = false) => {
    try {
      setLoading(true)
      const res = await getHSCodesFromHSDescriptions(axiosPrivate, {
        limit,
        offset: currentOffset,
        search,
      })

      const newItems = res.data.data

      if (newItems.length < limit) {
        setHasMore(false)
      } else {
        setHasMore(true)
      }

      if (isAppending) {
        setItems((prev) => {
          const existingData = new Set(prev.map((item) => item.data))
          const uniqueNewItems = newItems.filter((item: any) => !existingData.has(item.data))
          return [...prev, ...uniqueNewItems]
        })
      } else {
        setItems(newItems)
      }
    } catch (e) {
      console.error('Failed to fetch HS codes', e)
    } finally {
      setLoading(false)
    }
  }

  const debouncedSearch = useCallback(
    lodash.debounce((search: string) => {
      setOffset(0)
      fetchData(search, 0, false)
    }, 500),
    []
  )

  useEffect(() => {
    fetchData('', 0, false)
  }, [])

  useEffect(() => {
    setSelectedValue(value)
  }, [value])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchTerm(value)
    debouncedSearch(value)
  }

  const handleLoadMore = (e: React.MouseEvent) => {
    e.stopPropagation()
    const nextOffset = offset + limit
    setOffset(nextOffset)
    fetchData(searchTerm, nextOffset, true)
  }

  const handleSelect = (item: HSCodeType) => {
    onSelect(String(item.data))
    setSelectedValue(String(item.data))
  }

  const handleToggle = (isOpen: boolean) => {
    setIsDropdownOpen(isOpen)
    if (!isOpen && searchTerm !== '') {
      setSearchTerm('')
      setOffset(0)
      fetchData('', 0, false)
    }
  }

  const dropdownItem = items.map((item, index) => {
    return (
      <Dropdown.Item
        key={`${item.data}-${index}`}
        role='menuitem'
        size='sm'
        onClick={() => handleSelect(item)}
        title={String(item.data)}
        style={{whiteSpace: 'normal'}}
      >
        {String(item.data)}
      </Dropdown.Item>
    )
  })

  return (
    <Dropdown onToggle={handleToggle} show={isDropdownOpen}>
      <Dropdown.Toggle
        variant='label-dropdown'
        id=''
        className='btn btn-sm btn-bold dropdown-toggle fw-normal text-start w-100'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '44px',
          height: 'auto',
          whiteSpace: 'normal',
        }}
        disabled={disabled}
      >
        {selectedValue ? (
          <div
            style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {selectedValue}
          </div>
        ) : (
          placeholder
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu
        className='dropdown-econ-menu w-100'
        style={{maxHeight: '300px', overflowY: 'auto'}}
      >
        <div
          style={{
            padding: '4px 8px',
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 1,
          }}
        >
          <FormControl
            type='text'
            size='sm'
            placeholder='Search code...'
            className='dropdown-econ-search-bar'
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        {loading && offset === 0 ? (
          <div className='d-flex justify-content-center p-3'>
            <Spinner animation='border' size='sm' variant='primary' />
          </div>
        ) : (
          <>
            {items.length === 0 && !loading && (
              <div className='text-muted text-center p-2' style={{fontSize: '0.85rem'}}>
                No results found in database.
              </div>
            )}
            {dropdownItem}
            {hasMore && items.length > 0 && (
              <div className='text-center p-2'>
                <button
                  className='btn btn-sm btn-primary w-100'
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? <Spinner animation='border' size='sm' /> : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  )
}

export default HSDropdownWithLoadMore
